import Fuse from 'fuse.js';
import { flatten, uniq } from 'lodash/fp';
import { toast } from 'react-toastify';
import { all, debounce, fork, put, select, takeEvery } from 'redux-saga/effects';

import { resetPdfSearch, setSearchResults } from './actions';
import { getPdfSearchTerm, getPdfText, getScheduleStartPage, getSearchSettings } from './selectors';
import { PdfActionTypes, PdfPageText, PdfSearchResults, PdfSearchSettings, PDFSearchType } from './types';
import { highlightFunction, SentenceIndexes, splitPdfText } from './utils';

const uniqArrayOfIndexes = (indexes: number[][]) => indexes.reduce((acc: number[][], cur, currentIndex) => {
    const isAlreadyIncludedInAcc = cur.length === 1 && flatten(acc).includes(cur[0]);
    const isAlreadyIncludedInRemaining = cur.length === 1 && flatten(indexes.filter((_, index) => index > currentIndex)).includes(cur[0]);
    return (isAlreadyIncludedInAcc || isAlreadyIncludedInRemaining) ? acc : [...acc, cur];
}, []);

const getFuzzyMatchHighlights = (pdfPageText: PdfPageText[], searchTerm: string, pageToSearchFrom: number, threshold: number) => {
    const updatedPdfText: PdfPageText[] = pdfPageText.map(({ pageNumber, textItems }) => {
        if (pageNumber >= pageToSearchFrom) {
            const splitPageText = splitPdfText(textItems);

            const fuzzyMatchOptions = {
                includeScore: true,
                threshold,
                keys: ['sentence'],
                shouldSort: true
            };

            const entityFuse = new Fuse(splitPageText, fuzzyMatchOptions);
            const fuzzyMatches: Fuse.FuseResult<SentenceIndexes>[] = entityFuse.search(searchTerm);
            const results = fuzzyMatches.map(({ item, score }) => ({ value: item.sentence, indexes: item.nodeIndexes, score }));

            let searchHighlightIndexes: number[][] = [];
            if (results.length > 0) {
                const sortedResultIndexes = results.sort((a, b) => a.indexes[0] - b.indexes[0]).map(({ indexes }) => indexes);

                // As a fuzzy match of 0.6 may return multiple results for the same node index, we need to make sure we only have one per node if it is only one node highlighted
                // eg. node ['some text something'] - search for some - fuzzy match may return two matches both in the same node [[0], [0]], we only need to return it once.
                const uniqueIndexArrays = uniqArrayOfIndexes(sortedResultIndexes);

                searchHighlightIndexes = uniqueIndexArrays;
            }
            if (searchHighlightIndexes.length > 0) {
                return { pageNumber, textItems, searchHighlightIndexes };
            }
            return { pageNumber, textItems };
        }
        return { pageNumber, textItems };
    });
    return updatedPdfText;
};

const getExactMatchHighlights = (pdfPageText: PdfPageText[], searchTerm: string, pageToSearchFrom: number) => {
    const updatedPdfText: PdfPageText[] = pdfPageText.map(({ pageNumber, textItems }) => {
        if (pageNumber >= pageToSearchFrom) {
            const snippetToCheck = searchTerm.replace(/\s/g, '').toLowerCase();
            const textToSearch = textItems.map(({ str }) => str.replace(/\s/g, ''));
            let allIndexes: number[] = [];
            let nodeToStartSearchingFrom = 0;
            let allMatchesFound = false;

            while (!allMatchesFound && nodeToStartSearchingFrom <= (textItems.length - 1)) {
                // as the searchHighlightFunction returns after it has found a match, we want to keep
                // going through the document page for any other matches, until we have checked the entire page
                const highlightIndexes = highlightFunction(snippetToCheck, textToSearch, nodeToStartSearchingFrom);
                if (highlightIndexes.length > 0) {
                    const lastNodeHighlighted = highlightIndexes.sort((a, b) => b - a)[0];
                    nodeToStartSearchingFrom = lastNodeHighlighted + 1;
                } else {
                    nodeToStartSearchingFrom = textItems.length - 1;
                    allMatchesFound = true;
                }
                allIndexes = [...allIndexes, ...highlightIndexes];
            }
            return { pageNumber, textItems, searchHighlightIndexes: uniq(allIndexes).map(index => [index]).sort((a, b) => a[0] - b[0]) };
        }
        return { pageNumber, textItems };
    });
    return updatedPdfText;
};

export function* searchPdf() {
    try {
        const pdfPageText: PdfPageText[] = yield select(getPdfText);
        const searchTerm: string = yield select(getPdfSearchTerm);
        const { searchType, scheduleOnly }: PdfSearchSettings = yield select(getSearchSettings);
        const scheduleStartPage: number = yield select(getScheduleStartPage);
        const pageToSearchFrom = scheduleOnly ? scheduleStartPage : 1;
        const isExactMatch = searchType === PDFSearchType.EXACT;

        const updatedPdfText = isExactMatch ? getExactMatchHighlights(pdfPageText, searchTerm.toLowerCase(), pageToSearchFrom) : getFuzzyMatchHighlights(pdfPageText, searchTerm.toLowerCase(), pageToSearchFrom, searchType);
        const pagesWithHighlights = updatedPdfText.filter(({ searchHighlightIndexes }) => !!searchHighlightIndexes && searchHighlightIndexes.length > 0);

        const searchResults = pagesWithHighlights.reduce((acc: PdfSearchResults[], cur) => {
            const { pageNumber, searchHighlightIndexes } = cur;
            const pageHighlights: PdfSearchResults[] = searchHighlightIndexes!.map(indexes => ({ page: pageNumber, indexes }));
            return [...acc, ...pageHighlights];
        }, []);

        if (searchResults.length > 0) {
            const sortedResults = searchResults.sort((a, b) => a.page - b.page);
            yield put(setSearchResults(sortedResults));
        }
    } catch (e) {
        toast.error('We encountered a problem checking the pdf for the search term.');
    }
}

function* searchPdfWatcher() {
    yield debounce(1000, [PdfActionTypes.SET_PDF_SEARCH_TERM, PdfActionTypes.CLEAR_PDF_SEARCH_TERM, PdfActionTypes.APPLY_SEARCH_SETTINGS], searchPdf);
}

export function* resetSearch() {
    try {
        yield put(resetPdfSearch());
    } catch (e) {
        toast.error('We encountered a problem resetting the search term.');
    }
}

function* resetSearchWatcher() {
    yield takeEvery(PdfActionTypes.SET_INDEXES_TO_HIGHLIGHT, resetSearch);
}

export function* pdfSaga() {
    yield all([
        fork(searchPdfWatcher),
        fork(resetSearchWatcher)
    ]);
}
